/*
 * Automated Discount Handler for Rebuy Smart Cart
 *
 * Handles Shopify-managed automated discount codes (e.g., BLACKFRIDAY10, BLACKFRIDAY25, BLACKFRIDAY40)
 * that are applied at checkout and have line-level discount allocations in the cart data.
 *
 * Key Features:
 * - Reads discount data from line_level_discount_allocations (Shopify source of truth)
 * - Reads cart-level discount applications (URL/UTM discount codes)
 * - Fallback to parsing discount code from DOM (.rebuy-cart__flyout-item-discount-message)
 * - Calculates individual item discounted prices
 * - Updates DOM with correct sale prices and maintains original compare-at prices
 * - Manages footer discount display (tags + amount) for URL discount codes
 * - Handles multiple items with different prices, quantities, and variants
 * - Swiss locale price formatting (CHF X,XX)
 * - Always uses Shopify cart data as source of truth (no DOM-based price storage)
 * - Recalculates prices on every cart change for accurate updates
 *
 * Architecture:
 * - Subscribes to 'cart:change' event from RebuyEventManager
 * - Processes items with line_level_discount_allocations only
 * - Manages footer discount section for checkout-level (URL/UTM) discounts
 * - Updates DOM without interfering with other handlers (manual discounts, multiples promos)
 * - Compares compare-at-prices directly from Shopify cart item data
 *
 * Last Updated: November 2025
 */

window.AutomatedDiscountHandler = (function () {
  // Private variables
  let debug = true; // Enable debug logging to diagnose URL discount removal
  let currentDiscountCode = null;

  // Discount code to percentage mapping
  const DISCOUNT_CODE_MAP = {
    'BLACKFRIDAY40': 40,
    'BLACKFRIDAY25': 25,
    'BLACKFRIDAY10': 10,
  };

  function log(...args) {
    if (debug) {
      console.log('[AutomatedDiscountHandler]', ...args);
    }
  }

  /**
   * Parse a price string in Swiss locale format (CHF X,XX) to a number
   * Handles both formatted (CHF 47,99) and plain (47,99) formats
   */
  function parsePriceString(priceStr) {
    if (!priceStr) return null;
    return parseFloat(priceStr.replace('CHF', '').trim().replace(',', '.'));
  }

  /**
   * Format a number to Swiss locale price string (CHF X,XX)
   */
  function formatPrice(amount) {
    if (amount === null || amount === undefined) return null;
    const formatted = parseFloat(amount).toFixed(2);
    return 'CHF ' + formatted.replace('.', ',');
  }

  /**
   * Get compare-at price from Shopify cart item data
   * This is the authoritative source, not the DOM
   */
  function getCompareAtPriceFromCartItem(cartItem) {
    if (!cartItem.compare_at_price) return null;

    // Shopify stores compare_at_price in cents
    const compareAtPricePerUnit = cartItem.compare_at_price / 100;
    const quantity = cartItem.quantity || 1;

    // Return total compare-at price (per unit × quantity)
    return compareAtPricePerUnit * quantity;
  }

  /**
   * Extract discount code from the item's discount message DOM element
   * Returns the discount code if found (e.g., "BLACKFRIDAY40"), null otherwise
   */
  function extractDiscountCodeFromDOM(itemElement) {
    const discountMessage = itemElement.querySelector('.rebuy-cart__flyout-item-discount-message');
    if (!discountMessage) {
      log('No discount message element found in DOM');
      return null;
    }

    const code = discountMessage.textContent.trim().toUpperCase();
    log('Extracted discount code from DOM (.rebuy-cart__flyout-item-discount-message):', code);
    return code || null;
  }

  /**
   * Update the discount message DOM element with the correct code from Shopify
   * Fixes cases where Rebuy renders the wrong discount code
   */
  function updateDiscountMessageDOM(itemElement, correctCode) {
    const discountMessage = itemElement.querySelector('.rebuy-cart__flyout-item-discount-message');
    if (!discountMessage) return;

    const currentCode = discountMessage.textContent.trim().toUpperCase();
    if (currentCode !== correctCode) {
      log(`Correcting discount message in DOM from "${currentCode}" to "${correctCode}"`);
      discountMessage.textContent = correctCode;
    }
  }

  /**
   * Add banner notification when BlackFriday discount is applied
   */
  function addBlackFridayBanner(code) {
    // Don't add multiple banners
    if (document.getElementById('blackfriday-banner')) return;

    const cartItemsContainer = document.querySelector('[data-rebuy-component="cart-items"]');
    if (!cartItemsContainer) return;

    const banner = document.createElement('div');
    banner.id = 'blackfriday-banner';
    banner.style.cssText = `
      background-color: #f8f4f4;
      color: #d4335f;
      padding: 8px 15px;
      margin: 0 0 10px 0;
      border-radius: 4px;
      font-weight: bold;
      text-align: center;
      font-size: 14px;
      border-left: 3px solid #d4335f;
      z-index: 1000;
    `;

    banner.innerHTML = `🎉 Black Friday Angebot angewendet! 🎉`;
    cartItemsContainer.parentNode.insertBefore(banner, cartItemsContainer);
    log('BlackFriday banner added for code:', code);
  }

  /**
   * Remove banner notification
   */
  function removeBlackFridayBanner() {
    const banner = document.getElementById('blackfriday-banner');
    if (banner) {
      banner.remove();
    log('BlackFriday banner removed');
    }
  }

  /**
   * Get discount code from multiple sources
   * Priority: URL path > Cookie > Cart applications
   */
  function getDiscountCodeFromSources(cart) {
    // Priority 1: Check if the discount code is in the URL path (e.g., /discount/CODE)
    const urlPath = window.location.pathname;
    const discountPathMatch = urlPath.match(/\/discount\/([^/?]+)/i);
    if (discountPathMatch && discountPathMatch[1]) {
      log('Found discount code in URL path:', discountPathMatch[1]);
      return discountPathMatch[1];
    }

    // Priority 2: Check cookie (set by UTM parameters)
    const cookies = document.cookie.split(';');
    const discountCookie = cookies.find(c => c.trim().startsWith('discount_code='));
    if (discountCookie) {
      const value = discountCookie.split('=')[1];
      if (value && value !== 'undefined' && value.trim().length > 0) {
        log('Found discount code in cookie:', value.trim());
        return value.trim();
      }
    }

    // Priority 3: Check cart-level discount applications
    if (cart && cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
      const cartCode = cart.cart_level_discount_applications[0].title;
      if (cartCode) {
        log('Found discount code in cart applications:', cartCode);
        return cartCode;
      }
    }

    // Priority 4: Check line-level discount applications (if all items have the same code)
    if (cart && cart.items && cart.items.length > 0) {
      const lineCodes = cart.items
        .filter(item => item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0)
        .map(item => item.line_level_discount_allocations[0].discount_application?.title)
        .filter(code => code);

      // If all items have the same discount code, use it
      if (lineCodes.length > 0 && lineCodes.every(code => code === lineCodes[0])) {
        log('Found consistent discount code in line allocations:', lineCodes[0]);
        return lineCodes[0];
      }
    }

    return null;
  }

  /**
   * Get cart-level discount using centralized RebuyUtils
   * Returns object with code and discount amount or null
   */
  function getCartLevelDiscount() {
    if (!window.RebuyUtils || !window.RebuyUtils.getActiveDiscount) {
      log('RebuyUtils not available');
      return null;
    }

    const discount = window.RebuyUtils.getActiveDiscount();

    // Only return if this is a checkout-level discount (not manual Rebuy or automatic)
    if (discount && discount.source === 'checkout_level' && discount.code) {
      // Calculate amount from cart data
      const cart = window.Rebuy?.Cart?.cart;
      if (cart && cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        const amount = parseFloat(cart.cart_level_discount_applications[0].total_allocated_amount) / 100;
        log('Cart-level discount from RebuyUtils:', discount.code, 'Amount:', amount);
        return {
          code: discount.code,
          amount: amount,
          valueType: discount.type,
        };
      }
    }

    return null;
  }

  /**
   * Remove discount code from cart
   * Clears cookie and clicks Rebuy's native button (even if hidden) to trigger proper removal
   */
  function removeDiscountCode() {
    log('Attempting to remove discount code');

    // Clear discount code cookie (for URL/UTM codes)
    document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    log('Cleared discount_code cookie');

    // Find Rebuy's native remove button
    const discountInputWrapper = document.querySelector('[data-rebuy-component="discount-input"]');
    const nativeRemoveButton = discountInputWrapper?.querySelector('.rebuy-cart__discount-tag-remove');

    log('Discount input wrapper found:', !!discountInputWrapper);
    log('Native remove button found:', !!nativeRemoveButton);

    if (nativeRemoveButton) {
      // Check if wrapper is hidden (URL discount case)
      const wasHidden = discountInputWrapper.style.display === 'none' ||
                        window.getComputedStyle(discountInputWrapper).display === 'none';

      log('Wrapper was hidden:', wasHidden);

      if (wasHidden) {
        // Disconnect URLDiscountManager's observer to prevent re-hiding
        if (discountInputWrapper._observer) {
          discountInputWrapper._observer.disconnect();
          log('Disconnected URLDiscountManager observer');
        }

        log('Temporarily unhiding discount input to click native button');
        // Temporarily unhide to make button clickable
        discountInputWrapper.style.setProperty('display', 'block', 'important');

        // Small delay to ensure DOM updates
        setTimeout(() => {
          // Click the native button (let Rebuy handle removal)
          nativeRemoveButton.click();
          log('Clicked native Rebuy remove button');

          // Let URLDiscountManager handle re-hiding if needed
          // (cookie is cleared so it won't re-hide)
        }, 50);
      } else {
        // Button is visible, just click it
        log('Clicking visible native Rebuy remove button');
        nativeRemoveButton.click();
      }

      // Note: RebuyEventManager will detect this click and trigger 'discount:removed' event after 1000ms
      return;
    }

    // Fallback: Native button not found (URL codes might not populate it)
    log('Native remove button not found, using Rebuy SmartCart to clear discount');

    // Remove the footer UI immediately
    removeDiscountFooter();

    // Use Rebuy's SmartCart to apply empty discount code
    // This properly clears both cart and Shopify checkout session
    if (window.Rebuy && window.Rebuy.SmartCart) {
      log('Applying empty discount code via Rebuy SmartCart');

      // Try to use Rebuy's discount application method with empty code
      const discountInput = discountInputWrapper?.querySelector('input[type="text"]');
      if (discountInput) {
        // Set input to empty
        discountInput.value = '';
        log('Cleared discount input value');

        // Trigger Rebuy's apply button programmatically
        const applyButton = discountInputWrapper?.querySelector('button');
        if (applyButton) {
          // Temporarily show wrapper to make button functional
          if (discountInputWrapper._observer) {
            discountInputWrapper._observer.disconnect();
            log('Disconnected observer');
          }
          discountInputWrapper.style.setProperty('display', 'block', 'important');

          // Wait a moment then click apply with empty value
          setTimeout(() => {
            applyButton.click();
            log('Clicked apply button with empty discount');

            // Hide wrapper again after processing
            setTimeout(() => {
              // Let URLDiscountManager handle showing/hiding based on cookie state
              if (window.RebuyEventManager && window.RebuyEventManager.refreshCart) {
                window.RebuyEventManager.refreshCart();
                log('Cart refreshed after empty discount applied');
              }
            }, 1000);
          }, 100);
          return;
        }
      }
    }

    // Final fallback: just refresh cart
    log('Could not find Rebuy SmartCart controls, forcing cart refresh');
    if (window.RebuyEventManager && window.RebuyEventManager.refreshCart) {
      setTimeout(async () => {
        await window.RebuyEventManager.refreshCart();
        log('Cart refreshed as final fallback');
      }, 500);
    }
  }

  /**
   * Render discount footer with tags and amount
   * Matches the subtotal_custom alignment (label left, value right)
   */
  function renderDiscountFooter(discountData) {
    if (!discountData) {
      removeDiscountFooter();
      return;
    }

    const discountContainer = document.querySelector('[data-rebuy-component="discount-input"]');
    if (!discountContainer) return;

    // Remove existing footer if present
    removeDiscountFooter();

    // Create discount row matching subtotal_custom structure
    const rowDiv = document.createElement('div');
    rowDiv.id = 'automated-discount-footer-row';
    rowDiv.className = 'subtotal_custom';
    rowDiv.style.cssText = `
      padding-left: 20px;
      padding-right: 20px;
      border-top: none;
      border-bottom: none;
    `;

    // Create left side container for tags and code (takes full width, matches label behavior)
    const leftDiv = document.createElement('div');
    leftDiv.id = 'automated-discount-tags';
    leftDiv.className = 'rebuy-cart__discount-tags';
    leftDiv.style.cssText = `
      width: 100%;
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    const tagDiv = document.createElement('div');
    tagDiv.className = 'rebuy-cart__discount-tag';
    tagDiv.style.cssText = `
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: nowrap;
    `;
    tagDiv.innerHTML = `
      <span class="rebuy-cart__discount-tag-icon" style="display: flex; align-items: center; flex-shrink: 0; color: inherit;">
        <i class="fas fa-tags"></i>
      </span>
      <span class="rebuy-cart__discount-tag-text" style="font-weight: bold; flex-shrink: 0; white-space: nowrap; color: #000; font-size: 13px;">${discountData.code}</span>
      <span class="rebuy-cart__discount-tag-remove" style="cursor: pointer; display: flex; align-items: center; flex-shrink: 0; margin-left: 4px; color: #000;">
        <i class="far fa-times"></i>
      </span>
    `;

    leftDiv.appendChild(tagDiv);

    // Log for debugging
    log('Created tag with code:', discountData.code, 'HTML:', tagDiv.innerHTML);

    // Create right side for amount (matches price behavior)
    const amountDiv = document.createElement('div');
    amountDiv.id = 'automated-discount-amount';
    amountDiv.className = 'rebuy-cart__discount-amount';
    amountDiv.style.cssText = `
      font-weight: bold;
      color: #d4335f;
      white-space: nowrap;
      flex-shrink: 0;
    `;
    amountDiv.innerHTML = `- ${formatPrice(discountData.amount)}`;

    // Assemble row
    rowDiv.appendChild(leftDiv);
    rowDiv.appendChild(amountDiv);

    // Insert after discount container
    discountContainer.parentNode.insertBefore(rowDiv, discountContainer.nextSibling);

    // Remove borders from URLDiscountManager's notice if it exists
    const notice = document.getElementById('url-discount-notice');
    if (notice) {
      notice.style.borderTop = 'none';
      notice.style.borderBottom = 'none';
      notice.style.paddingTop = '0px';
      notice.style.paddingBottom = '0px';
      notice.style.paddingLeft = '20px';
      notice.style.paddingRight = '20px';
    }

    // Add click handler to remove button - use a small delay to ensure element is in DOM
    setTimeout(() => {
      const removeButton = tagDiv.querySelector('.rebuy-cart__discount-tag-remove');
      if (removeButton) {
        removeButton.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          log('Remove button clicked');
          removeDiscountCode();
        });
        log('Click handler attached to remove button');
      } else {
        log('Could not find remove button to attach handler');
      }
    }, 50);

    log('Discount footer rendered matching subtotal layout:', discountData);
  }

  /**
   * Remove discount footer elements
   */
  function removeDiscountFooter() {
    const row = document.getElementById('automated-discount-footer-row');

    if (row && row.parentNode) {
      row.remove();
      log('Discount footer row removed');
      return;
    }

    // Fallback for old structure (wrapper div)
    const wrapper = document.getElementById('automated-discount-footer-wrapper');
    if (wrapper && wrapper.parentNode) {
      wrapper.remove();
      log('Discount footer wrapper removed');
    }

    log('Discount footer removed');
  }

  /**
   * Get discount percentage for a given code
   * Uses fallback to parse percentage from code name if not in map
   */
  function getDiscountPercentageForCode(code) {
    if (!code) return 0;

    // Check if code is in mapping
    if (DISCOUNT_CODE_MAP[code]) {
      return DISCOUNT_CODE_MAP[code];
    }

    // Fallback: Try to parse percentage from code (e.g., "BLACKFRIDAY40" -> 40)
    const match = code.match(/(\d+)$/);
    if (match) {
      const percentage = parseInt(match[1], 10);
      if (percentage > 0 && percentage <= 100) {
        return percentage;
      }
    }

    return 0;
  }

  /**
   * Check if an item has line-level discount allocations (Shopify automated discount)
   */
  function hasLineItemDiscount(cartItem) {
    return (
      cartItem &&
      cartItem.line_level_discount_allocations &&
      Array.isArray(cartItem.line_level_discount_allocations) &&
      cartItem.line_level_discount_allocations.length > 0
    );
  }

  /**
   * Get discount amount for an item from line-level allocations
   * Returns discount amount in CHF (calculated from Shopify data in cents)
   */
  function getDiscountAmountFromLineAllocation(cartItem) {
    if (!hasLineItemDiscount(cartItem)) return 0;

    const allocation = cartItem.line_level_discount_allocations[0];
    if (!allocation || !allocation.amount) return 0;

    // Log the discount application data from Shopify
    if (allocation.discount_application) {
      log('Line-level discount from Shopify for item', cartItem.id, ':', {
        title: allocation.discount_application.title,
        type: allocation.discount_application.type,
        value: allocation.discount_application.value,
        value_type: allocation.discount_application.value_type,
        amount_cents: allocation.amount,
        amount_chf: allocation.amount / 100
      });
    }

    // Shopify stores amount in cents
    return allocation.amount / 100;
  }

  /**
   * Calculate discounted price for an item
   * Accounts for quantity - discount is applied to total line price (all units)
   * Priority: Use line-level allocation data, fallback to code-based calculation
   * Returns object with both discounted and original prices
   */
  function calculateDiscountedPrice(cartItem, itemElement) {
    const itemId = cartItem.id;
    const pricePerUnit = cartItem.price / 100; // Shopify stores in cents
    const quantity = cartItem.quantity || 1;
    const totalOriginalPrice = pricePerUnit * quantity;

    // Primary: Use line-level discount allocations from Shopify
    const discountAmount = getDiscountAmountFromLineAllocation(cartItem);
    if (discountAmount > 0) {
      const totalDiscountedPrice = totalOriginalPrice - discountAmount;
      log('Calculated price from line-level allocation:', {
        itemId,
        quantity,
        pricePerUnit,
        totalOriginalPrice,
        discountAmount,
        totalDiscountedPrice,
      });
      return {
        discountedPrice: Math.max(0, totalDiscountedPrice),
        originalPrice: totalOriginalPrice,
      };
    }

    // Fallback: Try to parse discount code from DOM and calculate
    const code = extractDiscountCodeFromDOM(itemElement);
    const percentage = getDiscountPercentageForCode(code);

    if (percentage > 0) {
      const totalDiscountedPrice = totalOriginalPrice * (1 - percentage / 100);
      log('Calculated price from code-based discount:', {
        itemId,
        quantity,
        code,
        percentage,
        totalOriginalPrice,
        totalDiscountedPrice,
      });
      return {
        discountedPrice: Math.round(totalDiscountedPrice * 100) / 100,
        originalPrice: totalOriginalPrice,
      };
    }

    return null; // No discount found
  }

  /**
   * Update the DOM for an item's price
   * Always shows original price as strikethrough and discounted price as sale price
   */
  function updateItemPriceDOM(cartItem, itemElement, discountedPrice, originalPrice) {
    const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
    if (!priceContainer) return;

    // Always show the original price as strikethrough with discounted price
    const priceHTML = `
      <div>
        <span class="rebuy-money sale">
          <span class="sr-only">Sale Price</span>
          <span>${formatPrice(discountedPrice)}</span>
        </span>
        <span class="rebuy-money compare-at">
          <span class="sr-only">Original Price</span>
          <span>${formatPrice(originalPrice)}</span>
        </span>
      </div>
    `;

    priceContainer.innerHTML = priceHTML;

    log('Updated price DOM for item', cartItem.id, {
      discountedPrice,
      originalPrice,
    });
  }

  /**
   * Process all cart items and update prices for those with line-level discounts
   * Also manages footer discount display for both cart-level and line-level discounts
   */
  function processCartItems(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      log('No cart items to process');
      removeBlackFridayBanner();
      removeDiscountFooter();
      currentDiscountCode = null;
      return;
    }

    // DEBUG: Log full cart discount data
    log('=== FULL CART DISCOUNT DEBUG ===');
    log('Cart applications:', cart.cart_level_discount_applications);
    if (cart.items && cart.items.length > 0) {
      cart.items.forEach(item => {
        if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
          log(`Item ${item.id} line-level discounts:`, item.line_level_discount_allocations);
        }
      });
    }
    log('================================');

    // Check for cart-level discount (URL/UTM code) using centralized RebuyUtils
    const cartLevelDiscount = getCartLevelDiscount();
    log('Cart-level discount detected:', cartLevelDiscount);

    // Get all item elements from DOM
    const itemElements = document.querySelectorAll('[data-item-id]');
    let hasBlackFridayDiscount = false;
    let detectedCode = null;
    let totalLineDiscount = 0;
    let lineDiscountCode = null;

    cart.items.forEach(cartItem => {
      // Find the corresponding DOM element for this item
      const itemElement = Array.from(itemElements).find(
        el => el.getAttribute('data-item-id') === String(cartItem.id)
      );

      if (!itemElement) {
        log('DOM element not found for item', cartItem.id);
        return;
      }

      // Check if item has a line-level discount
      if (!hasLineItemDiscount(cartItem)) {
        log('No line-level discount for item', cartItem.id);
        return;
      }

      // Get the discount code from Shopify's line allocation data (SOURCE OF TRUTH)
      const shopifyCode = cartItem.line_level_discount_allocations?.[0]?.discount_application?.title;

      // Extract discount code from DOM (for comparison/debugging)
      const domCode = extractDiscountCodeFromDOM(itemElement);

      log('Comparing discount codes for item', cartItem.id, ':', {
        fromDOM: domCode,
        fromShopify: shopifyCode,
        match: domCode === shopifyCode
      });

      // If Shopify code exists and differs from DOM, update the DOM to show the correct code
      if (shopifyCode && domCode !== shopifyCode) {
        updateDiscountMessageDOM(itemElement, shopifyCode);
      }

      // Check if Shopify code is a BlackFriday code (use Shopify as source of truth)
      if (shopifyCode && DISCOUNT_CODE_MAP[shopifyCode]) {
        hasBlackFridayDiscount = true;
        detectedCode = shopifyCode;
        log('BlackFriday discount detected, using code:', detectedCode);
      }

      // Accumulate line-level discount data for footer
      const discountAmount = getDiscountAmountFromLineAllocation(cartItem);
      if (discountAmount > 0) {
        totalLineDiscount += discountAmount;

        // Get the discount code from line allocation (Shopify is source of truth)
        if (!lineDiscountCode && shopifyCode) {
          lineDiscountCode = shopifyCode;
          log('Using Shopify discount code for footer:', lineDiscountCode);
        }
      }

      // Calculate discounted price (returns object with both prices)
      const priceData = calculateDiscountedPrice(cartItem, itemElement);
      if (priceData === null) {
        log('Could not calculate discounted price for item', cartItem.id);
        return;
      }

      // Update DOM with both discounted and original prices
      updateItemPriceDOM(cartItem, itemElement, priceData.discountedPrice, priceData.originalPrice);
    });

    // Handle BlackFriday banner display
    if (hasBlackFridayDiscount && detectedCode) {
      if (currentDiscountCode !== detectedCode) {
        currentDiscountCode = detectedCode;
        addBlackFridayBanner(detectedCode);
      }
    } else {
      removeBlackFridayBanner();
      currentDiscountCode = null;
    }

    // Handle footer discount display
    // Priority 1: Cart-level discounts (URL/UTM codes)
    // Priority 2: Discount code from URL path, cookie, or cart (when automatic discount is applied at line level)
    if (cartLevelDiscount) {
      log('Rendering footer for cart-level discount:', cartLevelDiscount.code);
      // Add a small delay to let URLDiscountManager finish hiding the input
      setTimeout(() => {
        renderDiscountFooter(cartLevelDiscount);
      }, 100);
    } else if (totalLineDiscount > 0) {
      // Get discount code from multiple sources (URL path, cookie, cart applications)
      const discountCode = getDiscountCodeFromSources(cart);

      // Use detected code or fallback to automatic discount code name
      const displayCode = discountCode || lineDiscountCode;

      log('Determining footer discount code:', {
        fromSources: discountCode,
        fromLineAllocation: lineDiscountCode,
        willDisplay: displayCode,
        totalDiscount: totalLineDiscount
      });

      if (displayCode) {
        const lineDiscountData = {
          code: displayCode,
          amount: totalLineDiscount,
          valueType: 'percentage'
        };
        log('Rendering footer for line-level discount:', lineDiscountData);
        setTimeout(() => {
          renderDiscountFooter(lineDiscountData);
        }, 100);
      } else {
        removeDiscountFooter();
      }
    } else {
      removeDiscountFooter();
    }
  }

  /**
   * Handle cart changes
   */
  function onCartChange(eventData) {
    const cart = eventData.cart;
    log('Cart change event received', { itemCount: cart?.items?.length });

    processCartItems(cart);
  }

  /**
   * Handle discount removed event
   */
  function onDiscountRemoved(eventData) {
    log('discount:removed event received');
    // Remove the footer display since discount is gone
    removeDiscountFooter();
    // Process cart items to ensure prices are correct
    if (eventData && eventData.cart) {
      processCartItems(eventData.cart);
    }
  }

  /**
   * Initialize the handler
   */
  function init() {
    // Subscribe to cart change events
    if (window.RebuyEventManager) {
      window.RebuyEventManager.on('cart:change', onCartChange)
        .on('cart:ready', onCartChange)
        .on('cart:show', onCartChange)
        .on('discount:removed', onDiscountRemoved);
      log('Initialized - subscribed to cart:change, cart:ready, cart:show, and discount:removed events');
    } else {
      console.error('[AutomatedDiscountHandler] RebuyEventManager not found');
    }
  }

  // Public API
  return {
    init: init,
    enableDebug: function () {
      debug = true;
      return this;
    },
    disableDebug: function () {
      debug = false;
      return this;
    },
  };
})();

// Auto-initialize when document is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    window.AutomatedDiscountHandler.init();
  });
} else {
  window.AutomatedDiscountHandler.init();
}
